import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ArticleSummary, ExperimentSummary, ProjectSummary } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { websiteJsonLd } from '@/lib/structured-data'
import { getFeaturedProjects } from '@/lib/content/projects'
import { getExperimentsByStatus } from '@/lib/content/lab'
import { getLatestArticles } from '@/lib/content/journal'
import HomeHero from '@/components/landing/home-hero'
import SelectedWork from '@/components/landing/selected-work'
import LabPreview from '@/components/landing/lab-preview'
import LatestJournal from '@/components/landing/latest-journal'
import AboutPreview from '@/components/landing/about-preview'
import ContactCta from '@/components/landing/contact-cta'

interface HomeProps {
    featured: Array<ProjectSummary>
    experiments: Array<ExperimentSummary>
    articles: Array<ArticleSummary>
}

const Home: FC<HomeProps> = ({ featured, experiments, articles }): ReactNode => {
    const activeExperiment = experiments.find((experiment) => experiment.status === 'active') ?? experiments[0] ?? null

    return (
        <Layout>
            <Seo path='/' jsonLd={websiteJsonLd()} />

            <HomeHero
                project={featured[0] ?? null}
                experiment={activeExperiment}
                article={articles[0] ?? null}
            />

            <SelectedWork projects={featured} />
            <LabPreview experiments={experiments} />
            <LatestJournal articles={articles} />
            <AboutPreview />
            <ContactCta />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    return {
        props: {
            featured: getFeaturedProjects(3),
            experiments: getExperimentsByStatus(),
            articles: getLatestArticles(3)
        }
    }
}

export default Home
